/**
 * The corner toast — .in's `.galert`.
 *
 * base.scss already carries the migrated rules (`.galert`, `.galert.shwalert`,
 * `#gClose`), so the markup below matches them element for element: a `> img`
 * sized by --_big-icon-size (24px), a `> p`, and the `#gClose` cross.
 *
 * Three deliberate departures from .in's assets-src/js/@common/utils/alert.js:
 *
 *  1. Vanilla, not cash-dom. Every other module under @common/ is, and the
 *     whole file is four DOM calls.
 *  2. A different signature. .in's is
 *     `ShowAlert(context, message, image, emotion, fav)` where the last two
 *     switch on a saved-coupons variant that links to `/profile/` — a page
 *     this app does not have. Nothing here calls that variant, so porting the
 *     branch would ship a dead code path with a dead link inside it.
 *  3. The icon is a data URI, not a CDN image. .in passes
 *     cdn.grabon.in/…/g-e.png; that host is India's and the file is not
 *     mirrored on images.grabon.com (checked: 404). Rather than invent an
 *     International asset, the default icons are inline SVGs — the same
 *     check-circle / x-circle pair the Next.js ToastAlert renders through
 *     lucide. Callers that want a brand image pass `image`.
 *
 * .in appends and leaves any existing toast in place, which duplicates the
 * `#gClose` id. Here a new toast replaces the old one.
 */

/** .in dismisses after 8s. */
const AUTO_DISMISS_MS = 8000;

const ICONS = {
  success: icon('#16a34a', '<path d="M21.8 10A10 10 0 1 1 17 3.3"/><path d="m9 11 3 3L22 4"/>'),
  error: icon(
    '#dc2626',
    '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
  ),
};

function icon(color, paths) {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" ` +
    `fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" ` +
    `stroke-linejoin="round">${paths}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * @param {string} message   plain text — inserted as textContent, never HTML
 * @param {{type?: 'success'|'error', image?: string, timeout?: number}} [options]
 */
export function ShowAlert(message, options) {
  const opts = options || {};
  const type = opts.type === 'error' ? 'error' : 'success';
  const host = document.getElementById('gBody') || document.body;
  if (!host || !message) return;

  RemoveAlert();

  const alert = document.createElement('div');
  alert.className = 'galert';

  const image = document.createElement('img');
  image.src = opts.image || ICONS[type];
  image.alt = '';
  // Decorative: the message beside it already says everything.
  image.setAttribute('aria-hidden', 'true');

  const text = document.createElement('p');
  // textContent, not innerHTML. .in interpolates the message straight into a
  // template string; ours can carry a server-supplied `message` field, and that
  // is exactly the path an injected <img onerror> would take.
  text.textContent = message;

  const close = document.createElement('div');
  close.id = 'gClose';
  close.setAttribute('role', 'button');
  close.setAttribute('aria-label', 'Dismiss notification');
  close.innerHTML =
    '<svg width="13" height="13" viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill="#A4A5A5" d="M12.6095584,1.30057214 L7.50681905,6.4033115 L12.6095584,11.5060509 ' +
    'C13.2521807,12.1485188 12.155805,13.2591401 11.5060615,12.6095478 L6.4033221,7.50680846 ' +
    'L1.30058273,12.6095478 C0.658114751,13.2520158 -0.452506476,12.1557944 0.197085778,11.5060509 ' +
    'L5.29982514,6.4033115 L0.197085778,1.30057214 C-0.445382201,0.658104159 0.650993503,-0.452517069 ' +
    '1.30058273,0.197075185 L6.4033221,5.29981455 L11.5060615,0.197075185 ' +
    'C12.155805,-0.452514042 13.2521747,0.658104159 12.6095584,1.30057214 Z"/></svg>';
  close.addEventListener('click', RemoveAlert, { once: true });

  // The order base.scss expects: image, text, then the absolutely-placed cross.
  alert.appendChild(image);
  alert.appendChild(text);
  alert.appendChild(close);
  // Announced without stealing focus, since the toast is never focused.
  alert.setAttribute('role', 'status');
  alert.setAttribute('aria-live', 'polite');
  host.appendChild(alert);

  // `.shwalert` is what flips display:none to flex — added after the append so
  // the browser has a frame to paint the hidden state first.
  alert.classList.add('shwalert');

  const timeout = typeof opts.timeout === 'number' ? opts.timeout : AUTO_DISMISS_MS;
  if (timeout > 0) setTimeout(RemoveAlert, timeout);
}

export function RemoveAlert() {
  const existing = document.querySelectorAll('.galert');
  for (let i = 0; i < existing.length; i += 1) existing[i].remove();
}
